import { useMutation } from '@tanstack/react-query';
import apiService from '../../services/apiService';
import analyticsService from '../../services/analyticsService';
import { useAuthStore } from '../../stores/authStore';
import { tokenStorage } from '../../lib/tokenStorage';
import type { LoginRequest, LoginResponse, GoogleAuthResponse } from '../../types';

export function useLogin() {
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => apiService.login(credentials),
    onSuccess: (data: LoginResponse) => {
      setAuth(data.user, data.access_token);
      analyticsService.identify(data.user.id, { email: data.user.email, role: data.user.role, method: 'email' });
      analyticsService.track('login', { method: 'email' });
    },
  });
}

export function useGoogleAuth() {
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: async (input: string | { token: string; tokenType: 'id_token' | 'access_token' }) => {
      const body = typeof input === 'string'
        ? { idToken: input }
        : input.tokenType === 'access_token'
          ? { accessToken: input.token }
          : { idToken: input.token };
      const response = await apiService.post('/auth/google/auth', body);
      return response.data;
    },
    onSuccess: (data) => {
      const result = data.data as GoogleAuthResponse;

      tokenStorage.setTokens(result.AccessToken, result.RefreshToken, result.IdToken);

      // Decode user from ID token
      try {
        const payload = result.IdToken.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        const user = {
          id: decoded.sub || decoded['cognito:username'],
          email: decoded.email,
          firstName: decoded.given_name || 'User',
          lastName: decoded.family_name || '',
          role: decoded['cognito:groups']?.[0] || 'user',
          avatar: '',
          username: decoded['cognito:username'],
        };
        tokenStorage.setUser(user);
        setAuth(user, result.AccessToken);
        analyticsService.identify(user.id, { email: user.email, role: user.role, method: 'google' });
        analyticsService.track('login', { method: 'google' });
      } catch {
        // Profile completion may be needed
      }
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: { email: string; password: string; firstName: string; lastName: string }) =>
      apiService.register(data),
    onSuccess: () => {
      analyticsService.track('signup', { method: 'email' });
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (data: { email: string; code: string }) => apiService.verifyEmail(data),
  });
}

export function useResendVerifyEmail() {
  return useMutation({
    mutationFn: (email: string) => apiService.resendVerifyEmail(email),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => apiService.forgotPassword(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: { email: string; code: string; password: string }) =>
      apiService.resetPassword(data),
  });
}
